import { useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Navigation } from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);
    const { settings } = useSettings();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            toast.success("Message received. The kingdom will respond shortly.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatErr(err));
        } finally { setLoading(false); }
    };

    return (
        <div data-testid="contact-page" className="grain">
            <section className="border-b border-line py-16 md:py-24 bg-bg-soft">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase font-bold mb-4">Get In Touch</div>
                    <h1 className="h-display text-6xl md:text-8xl">Contact The Kings</h1>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-2 gap-12">
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                    <div>
                        <label className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold">Name</label>
                        <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm" />
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold">Email</label>
                        <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm" />
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold">Subject</label>
                        <input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm" />
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold">Message</label>
                        <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm" />
                    </div>
                    <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-gold px-7 py-4 text-xs disabled:opacity-60">
                        {loading ? "Sending…" : "Send Message"}
                    </button>
                </form>

                <div className="space-y-6">
                    <div className="card-flat p-6">
                        <div className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold mb-4">Home Court</div>
                        <div className="flex items-start gap-3">
                            <MapPin className="text-gold mt-1" size={18} />
                            <div className="flex-1">
                                <div className="h-display text-lg leading-snug">{settings?.contact_address || "Houston, Texas"}</div>
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(settings?.contact_address || "Stellar Bank Gym Fulshear High School Fulshear TX")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="contact-directions"
                                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-line hover:border-gold text-xs tracking-[0.2em] uppercase font-bold text-white hover:text-gold transition-colors"
                                >
                                    <Navigation size={12} className="text-gold" />
                                    Get Directions
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="card-flat p-6">
                        <div className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold mb-4">Reach Us</div>
                        <div className="flex items-center gap-3 mb-3">
                            <Mail className="text-gold" size={18} />
                            <a href={`mailto:${settings?.contact_email || "info@texaspurplekings.com"}`} className="text-sm hover:text-gold">{settings?.contact_email || "info@texaspurplekings.com"}</a>
                        </div>
                        <div className="flex items-center gap-3">
                            <Phone className="text-gold" size={18} />
                            <span className="text-sm">{settings?.contact_phone || "+1 (713) 555-KING"}</span>
                        </div>
                    </div>
                    <div className="card-flat p-6">
                        <div className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold mb-4">Working With Us</div>
                        <p className="text-ink text-sm leading-relaxed">
                            For sponsorship, media, ticketing, or partnership inquiries — drop us a message and the front office will respond within 48 hours.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
